import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Label } from 'recharts';
import { interpolateRainbow } from 'd3-scale-chromatic';
import _ from 'lodash'

const parseDate = (dateString) => new Date(dateString + '-01');
const formatMonth = (dateString) => {
    const [year, month] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1);
    return date.toLocaleString('en-us', { month: 'long', year: 'numeric' });
};

const monthOrder = {
    January: 1, February: 2, March: 3,
    April: 4, May: 5, June: 6,
    July: 7, August: 8, September: 9,
    October: 10, November: 11, December: 12
};

const darkenColor = (color, factor) => {
    const rgb = color.match(/\d+/g).map(Number);
    const r = Math.max(0, Math.min(255, Math.floor(rgb[0] * factor)));
    const g = Math.max(0, Math.min(255, Math.floor(rgb[1] * factor)));
    const b = Math.max(0, Math.min(255, Math.floor(rgb[2] * factor)));
    return `rgb(${r},${g},${b})`;
};

const getColor = (i, numSpecies) => {
    const color = interpolateRainbow(i / (numSpecies - 1));
    return darkenColor(color, 0.7);
};

export default function AbundanceLineGraph({ data }) {
    const [numMonths, setNumMonths] = useState(4);

    const jsonData = JSON.stringify(data);
    const parsedData = JSON.parse(jsonData);
    const sortedData = parsedData.slice().sort((a, b) => parseDate(a.date) - parseDate(b.date));
    const uniqueMonths = Array.from(new Set(sortedData.map(entry => entry.date)))
        .sort((a, b) => parseDate(a) - parseDate(b));
    const lastMonths = uniqueMonths.slice(-numMonths);
    const filteredData = sortedData.filter(entry => lastMonths.includes(entry.date));

    const processData = (data) => {
        const allSpecies = new Set();
        data.forEach(entry => {
            entry.species.forEach(({ species }) => {
                allSpecies.add(_.capitalize(species));
            });
        });
        const initialCounts = {};
        allSpecies.forEach(species => {
            initialCounts[species] = 0;
        });

        return data.map(entry => {
            const monthName = formatMonth(entry.date);
            const monthData = { name: monthName, ...initialCounts };
            entry.species.forEach(({ species, count }) => {
                const capitalizedSpecies = _.capitalize(species);
                monthData[capitalizedSpecies] = (monthData[capitalizedSpecies] || 0) + count;
            });
            return monthData;
        });
    };

    const graphData = processData(filteredData).sort((a, b) => {
        const monthA = monthOrder[a.name];
        const monthB = monthOrder[b.name];
        return monthA - monthB;
    });
    const numSpecies = new Set(graphData.flatMap(item => Object.keys(item).slice(1))).size;
    const allSpecies = new Set(graphData.flatMap(item => Object.keys(item).slice(1)).map(species => _.capitalize(species)));
    const lines = Array.from(allSpecies).map((species, index) => (
        <Line
            key={index}
            type="monotone"
            dataKey={species}
            stroke={getColor(index, numSpecies)}
            activeDot={{ r: 8 }} />
    ));

    return (
        <div style={{}}>
            <div style={{
                marginTop: '20px', textAlign: 'center', padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                display: 'inline-block',
            }}>
                <label htmlFor="numMonths">Previous no. of months: </label>
                <select
                    id="numMonths"
                    value={numMonths}
                    onChange={(e) => setNumMonths(Number(e.target.value))}
                >
                    {[...Array(12).keys()].map(num => (
                        <option key={num + 1} value={num + 1}>
                            {num + 1}
                        </option>
                    ))}
                </select>
            </div>
            <div style={{ marginTop: '20px', height: '350px', width: '90%', fontSize: '11px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        width={500}
                        height={300}
                        data={graphData}
                        margin={{
                            top: 5,
                            right: 5,
                            left: 5,
                            bottom: 20,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name">
                            <Label value="Month/Year" offset={0} position="insideBottom" dy={10} style={{ fontSize: '15px', fontWeight: 'bold' }} />
                        </XAxis>
                        <YAxis>
                            <Label value="Count" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fontSize: '15px', fontWeight: 'bold' }} />
                        </YAxis>
                        <Tooltip />
                        <Legend
                            wrapperStyle={{
                                bottom: 0,
                                marginTop: 20,
                                fontSize: '13px',
                            }}
                        />
                        {lines}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}